import { Geometry2, WellKnownTextString } from "../geom"

export type GeometryFormatId =
    // Well-known text (WKT)
    | "wkt"
    // Well-known binary as base64
    | "wkb"
    // Custom binary format v1
    | "binary1"

export function getDefaultGeometryFormat(defaultFormat?: GeometryFormatId): GeometryFormatId {

    const params = new URLSearchParams(location.search)

    const format = params.get("geometryFormat")

    switch (format) {
        case "wkt":
        case "wkb":
        case "binary1":
            return format;

        default:
            return defaultFormat || "wkb"
    }

}

export function hasBinaryResponses(format: GeometryFormatId) {

    return (format === "binary1")
}


export function readGeometriesFromOmniaBinaryToIndex<Payload extends { Geometries: { [key: number]: Geometry2 } }>(buffer: ArrayBufferLike) {

    const { geometries, payload } = readGeometriesFromOmniaBinary<Payload>(buffer)

    const g = {}
    let i = 0
    for (const c of geometries) {
        g[++i] = c
    }

    payload.Geometries = g

    return payload

}

export function readGeometriesFromOmniaBinary<Payload>(buffer: ArrayBufferLike) {

    const reader = createBufferReader(buffer)

    const geometries = readGeometries(reader)

    const json = reader.readRemainingText()

    const payload = json ? JSON.parse(json) as Payload : undefined

    return {
        geometries,
        payload
    }

    function readGeometries(reader: ReturnType<typeof createBufferReader>) {

        const geometries = reader.getInt32()

        const geometriesArray: (number[][] | WellKnownTextString)[] = Array(geometries)

        for (let geometry = 0; geometry < geometries; geometry++) {

            const rings = reader.getUint16()

            if (rings === 0) {

                const length = reader.getInt32()
                const wkt = reader.readText(length)
                geometriesArray[geometry] = wkt

            }
            else {

                const ringsArray = Array(rings)

                for (let ring = 0; ring < rings; ring++) {

                    const coords = reader.getInt32()

                    const coordsArray = Array(coords) as number[]

                    for (let c = 0; c < coords; c++) {
                        const v = reader.getFloat64()
                        coordsArray[c] = v
                    }

                    ringsArray[ring] = coordsArray

                }

                geometriesArray[geometry] = ringsArray

            }

        }

        return geometriesArray

    }

}


/**
 * Creates a basic reader from a ArrayBufferLike
 */
function createBufferReader(buffer: ArrayBufferLike) {

    let offset = 0

    const view = new DataView(buffer)

    return {

        getInt32() {
            const r = view.getInt32(offset, true)
            offset += 4
            return r
        },

        getUint16() {
            const r = view.getUint16(offset, true)
            offset += 2
            return r
        },

        getFloat64() {
            const r = view.getFloat64(offset, true)
            offset += 8
            return r
        },

        readRemaining() {
            return buffer.slice(offset)
        },

        readRemainingText() {
            const decoder = new TextDecoder()
            return decoder.decode(buffer.slice(offset))
        },

        readText(length: number) {
            const decoder = new TextDecoder()
            const slice = buffer.slice(offset, offset + length)
            offset += length
            return decoder.decode(slice)
        }

    }

}
